import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LPressable from "@components/LPressable";
import LScreen from "@components/LScreen";
import LText from "@components/LText";
import { useAuthSignOut } from "@utils/firebaseAuth/query/auth";
import { auth } from "@utils/firebase";

import { AUTH_QUERY_KEY, USER_EXISTS_IN_DB_QUERY_KEY } from "@utils/queryKeys";

import * as Intercom from "@utils/intercom";
import { captureException } from "@utils/captureException";
import { useAuth } from "@utils/firebaseAuth/auth";
import { USER_ME_QUERY_KEY } from "@hooks/trpc/useMe";
import { setPendingEmailVerificationToken } from "@utils/emailVerification/pendingToken";
import { logger } from "@utils/logger";
import { useAuthSession, useData, useRuntime } from "../../runtime/provider";
import {
  BottomSheetActionLayout,
  BottomSheetSubmitButton,
} from "@utils/swmModal/components/BottomSheetPrimitives";
import AppModalSheet from "@utils/appModal/components/AppModalSheet";
import LoadingIndicator from "./LoadingIndicator";
import { fonts, theme } from "@theme";
import * as IntentLauncher from "expo-intent-launcher";
import { router, useLocalSearchParams, usePathname } from "expo-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Linking, Platform, StyleSheet } from "react-native";
import { FadeInDown } from "react-native-reanimated";
import {
  type SheetAdapterRef,
  useBottomSheetManager,
} from "react-native-bottom-sheet-stack";
import { toast } from "sonner-native";
import { trackOnb } from "./analytics/trackOnboarding";

type VerifyState = "idle" | "confirming" | "error";
type EmailApp = "mail" | "gmail";

const AUTH_RESTORE_GRACE_MS = 2500;
const GMAIL_ANDROID_PACKAGE = "com.google.android.gm";

const getEmailAppUrl = (app: EmailApp) => {
  if (app === "gmail") {
    return (
      Platform.select({
        android: "https://mail.google.com/mail/u/0/#inbox",
        default: "googlegmail://",
      }) ?? "googlegmail://"
    );
  }

  return Platform.OS === "ios" ? "message://" : "mailto:";
};

const getErrorMessage = (error: unknown) => {
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string" &&
    (error.message.includes("invalid") ||
      error.message.includes("expired") ||
      error.message.includes("already been used"))
  ) {
    return "This verification link is invalid or has expired. Please request a new one.";
  }

  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string" &&
    error.message.includes("another account")
  ) {
    return "This verification link could not be used with this account. Go back to login and sign in with the email that received it.";
  }

  return "We could not confirm your email. Please try again or request a new link.";
};

const now = () => globalThis.performance?.now?.() ?? Date.now();

const logTiming = (
  label: string,
  flowStartedAt: number,
  stepStartedAt?: number,
  extra?: Record<string, unknown>,
) => {
  const current = now();
  console.log("::EmailVerificationTiming::", label, {
    elapsedMs: Math.round(current - flowStartedAt),
    ...(stepStartedAt
      ? { stepMs: Math.round(current - stepStartedAt) }
      : undefined),
    ...extra,
  });
};

const trackEmailVerification = (
  action: string,
  props?: Record<string, unknown>,
) => {
  logger.info("auth", `email-verification-${action}`, props);
  trackOnb("signup", "email-verification", action, props);
  captureException(action, {
    channel: "breadcrumb",
    area: "auth",
    operation: `email-verification-${action}`,
    extra: props,
  });
};

const ShellButton = ({
  disabled = false,
  label,
  onPress,
  sentryEventName,
}: {
  disabled?: boolean;
  label: string;
  onPress: () => void;
  sentryEventName: string;
}) => (
  <LBox opacity={disabled ? 0.4 : 1}>
    <LPressable
      fadeEnabled
      sentryEventName={sentryEventName}
      onPress={onPress}
      disabled={disabled}
      style={{
        backgroundColor: theme.colors.white,
        borderRadius: 9999,
        paddingVertical: theme.spacing.s4,
        paddingHorizontal: theme.spacing.s4,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LText
        fontSize={16}
        fontFamily={fonts.semiBold}
        color="screenBg"
        textAlign="center"
      >
        {label}
      </LText>
    </LPressable>
  </LBox>
);

const BackToLoginSheet = React.forwardRef<
  SheetAdapterRef,
  {
    onLogOut: () => Promise<void>;
    onResendEmail: () => Promise<void>;
  }
>(function BackToLoginSheet({ onLogOut, onResendEmail }, ref) {
  return (
    <AppModalSheet ref={ref} showHandle={false}>
      {({ closeModal }) => (
        <BottomSheetActionLayout
          title="Back to Login?"
          subtitle="You will have to sign in again."
          actions={
            <LBox gap="s3">
              <BottomSheetSubmitButton
                label="Resend Email"
                sentryEventName="email_verification_resend_email_from_sheet"
                onPress={() => {
                  closeModal();
                  onResendEmail().catch((error: unknown) => {
                    captureException(error, {
                      channel: "error",
                      area: "auth",
                      operation: "email-verification-resend-from-sheet",
                    });
                  });
                }}
              />
              <LPressable
                fadeEnabled
                sentryEventName="email_verification_log_out_from_sheet"
                onPress={() => {
                  closeModal();
                  onLogOut().catch((error: unknown) => {
                    captureException(error, {
                      channel: "error",
                      area: "auth",
                      operation: "email-verification-log-out-to-login",
                    });
                  });
                }}
                style={{
                  alignItems: "center",
                  justifyContent: "center",
                  paddingVertical: theme.spacing.s3,
                }}
              >
                <LText
                  color="danger"
                  fontFamily={fonts.semiBold}
                  fontSize={16}
                  lineHeight={22}
                  textAlign="center"
                >
                  Log Out
                </LText>
              </LPressable>
            </LBox>
          }
        />
      )}
    </AppModalSheet>
  );
});

export const EmailVerificationScreen = () => {
  const session = useData();
  const { kv } = useRuntime().ports;
  const { mode, token } = useLocalSearchParams<{
    mode?: string;
    token?: string;
  }>();
  const isInvalidMode = !!mode && mode !== "verifyEmail";
  const isEmailVerificationTokenRoute = !!token && !isInvalidMode;
  const {
    email: authEmail,
    firebaseUser,
    status,
  } = useAuth({
    suppressEmailVerificationResend: isEmailVerificationTokenRoute,
  });
  const [verifyState, setVerifyState] = useState<VerifyState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [authRestoreGraceElapsedToken, setAuthRestoreGraceElapsedToken] =
    useState<string | null>(null);
  const processedTokenRef = useRef<string | null>(null);
  const backSheetIdRef = useRef<string | null>(null);
  const pathname = usePathname();
  const bottomSheetManager = useBottomSheetManager();
  const authSession = useAuthSession();
  const signOutMutation = useAuthSignOut(auth);

  const displayEmail = authEmail ?? firebaseUser?.email;
  const emailLabel = useMemo(
    () => displayEmail ?? "your email address",
    [displayEmail],
  );
  const authRestoreGraceElapsed = authRestoreGraceElapsedToken === token;
  const isWaitingForAuth =
    !!token &&
    !isInvalidMode &&
    (status === "firebase-auth-pending" ||
      status === "firebase-user-logged-in" ||
      status === "db-user-pending" ||
      (status === "no-user" && !authRestoreGraceElapsed));
  const isProcessing = verifyState === "confirming" || isWaitingForAuth;

  const continueToOnboarding = useCallback(() => {
    trackEmailVerification("continue-to-onboarding");
    router.replace("/onboarding");
  }, []);

  const logOutToLogin = useCallback(async () => {
    trackEmailVerification("logout-to-login-pressed", {
      hasCurrentUser: !!auth.currentUser,
      state: verifyState,
    });
    if (auth.currentUser) {
      await signOutMutation.mutateAsync();
    }
    session.queryClient.removeQueries({ queryKey: [AUTH_QUERY_KEY] });
    session.queryClient.removeQueries({
      queryKey: [USER_EXISTS_IN_DB_QUERY_KEY],
    });
    router.replace("/login");
  }, [signOutMutation, verifyState]);

  const resendVerificationEmail = useCallback(async () => {
    if (isResendingEmail) {
      return;
    }

    setIsResendingEmail(true);
    trackEmailVerification("resend-email-pressed", {
      authStatus: status,
      state: verifyState,
    });

    try {
      await session.api.user.onboarding.resendEmailVerification.mutate();
      trackEmailVerification("resend-email-succeeded", {
        authStatus: status,
        state: verifyState,
      });
      toast.success("Email sent");
    } catch (error) {
      trackEmailVerification("resend-email-failed", {
        authStatus: status,
        state: verifyState,
        error: error instanceof Error ? error.message : String(error),
      });
      captureException(error, {
        channel: "error",
        area: "auth",
        operation: "resend-email-verification",
      });
      toast.error("Could not resend email. Please try again.");
    } finally {
      setIsResendingEmail(false);
    }
  }, [isResendingEmail, status, verifyState]);

  const confirmBackToLogin = useCallback(() => {
    if (isProcessing) {
      return;
    }

    trackEmailVerification("back-to-login-alert-opened", {
      authStatus: status,
      state: verifyState,
    });

    if (backSheetIdRef.current) {
      bottomSheetManager.close(backSheetIdRef.current);
      backSheetIdRef.current = null;
    }

    backSheetIdRef.current = bottomSheetManager.open(
      <BackToLoginSheet
        onLogOut={async () => {
          try {
            await logOutToLogin();
          } catch (error) {
            captureException(error, {
              channel: "error",
              area: "auth",
              operation: "email-verification-log-out-to-login",
            });
          }
        }}
        onResendEmail={async () => {
          try {
            await resendVerificationEmail();
          } catch (error) {
            captureException(error, {
              channel: "error",
              area: "auth",
              operation: "email-verification-resend-from-sheet",
            });
          }
        }}
      />,
      { backdrop: true, mode: "push" },
    );
  }, [
    bottomSheetManager,
    isProcessing,
    logOutToLogin,
    resendVerificationEmail,
    status,
    verifyState,
  ]);

  useEffect(() => {
    if (pathname === "/auth/verify-email" || !backSheetIdRef.current) {
      return;
    }

    bottomSheetManager.close(backSheetIdRef.current);
    backSheetIdRef.current = null;
  }, [bottomSheetManager, pathname]);

  useEffect(
    () => () => {
      if (backSheetIdRef.current) {
        bottomSheetManager.close(backSheetIdRef.current);
        backSheetIdRef.current = null;
      }
    },
    [bottomSheetManager],
  );

  const completeOnServer = useCallback(
    async (verificationToken?: string, flowStartedAt?: number) => {
      const authReadyStartedAt = now();
      await authSession.waitForReady();
      if (flowStartedAt) {
        logTiming("auth-ready-before-api", flowStartedAt, authReadyStartedAt, {
          hasCurrentUser: !!auth.currentUser,
          hasToken: !!verificationToken,
        });
      }
      trackEmailVerification("auth-ready-before-api", {
        hasCurrentUser: !!auth.currentUser,
        hasToken: !!verificationToken,
      });
      if (!auth.currentUser) {
        throw new Error("Auth user is not ready for email verification");
      }

      const apiStartedAt = now();
      await session.api.user.onboarding.completeEmailVerification.mutate(
        verificationToken ? { token: verificationToken } : undefined,
      );
      if (flowStartedAt) {
        logTiming("api-complete", flowStartedAt, apiStartedAt, {
          hasToken: !!verificationToken,
        });
      }
      trackEmailVerification("api-complete", {
        hasToken: !!verificationToken,
      });

      const cacheStartedAt = now();
      session.queryClient.setQueryData(
        [USER_EXISTS_IN_DB_QUERY_KEY],
        (
          old:
            | {
                onboardingStatus?: string;
                onboardingStep?: string;
              }
            | undefined,
        ) =>
          old
            ? {
                ...old,
                onboardingStatus: "onboarding_required",
                onboardingStep: "setup_pending",
              }
            : old,
      );
      session.queryClient.setQueryData(
        USER_ME_QUERY_KEY,
        (
          old:
            | {
                onboarded?: boolean;
                onboardingStep?: string;
              }
            | undefined,
        ) =>
          old
            ? {
                ...old,
                onboarded: false,
                onboardingStep: "setup_pending",
              }
            : old,
      );
      if (flowStartedAt) {
        logTiming("cache-update", flowStartedAt, cacheStartedAt);
      }

      const invalidationStartedAt = now();
      void Promise.all([
        session.queryClient.invalidateQueries({
          queryKey: [USER_EXISTS_IN_DB_QUERY_KEY],
        }),
        session.queryClient.invalidateQueries({ queryKey: USER_ME_QUERY_KEY }),
      ])
        .then(() => {
          if (flowStartedAt) {
            logTiming(
              "background-invalidation-complete",
              flowStartedAt,
              invalidationStartedAt,
            );
          }
        })
        .catch((error: unknown) => {
          captureException(error, {
            channel: "error",
            area: "auth",
            operation: "verify-email-cache-invalidation",
          });
        });
    },
    [authSession],
  );

  const processToken = useCallback(
    async (verificationToken: string) => {
      const flowStartedAt = now();
      logTiming("start", flowStartedAt, undefined, {
        authStatus: status,
        hasFirebaseUser: !!firebaseUser,
      });
      trackEmailVerification("confirm-started", {
        authStatus: status,
        hasFirebaseUser: !!firebaseUser,
      });
      setVerifyState("confirming");
      setErrorMessage(null);

      try {
        if (status !== "logged-in" || !firebaseUser) {
          logTiming("signed-out-before-complete", flowStartedAt);
          setPendingEmailVerificationToken(kv, verificationToken);
          trackEmailVerification("signed-out-redirect", {
            storedPendingToken: true,
            authStatus: status,
          });
          await logOutToLogin();
          return;
        }

        await completeOnServer(verificationToken, flowStartedAt);
        trackEmailVerification("confirmed");
        logTiming("navigate-onboarding", flowStartedAt);
        continueToOnboarding();
      } catch (error) {
        logTiming("error", flowStartedAt, undefined, {
          error:
            error instanceof Error
              ? { name: error.name, message: error.message }
              : String(error),
        });
        trackEmailVerification("confirm-error", {
          error: error instanceof Error ? error.message : String(error),
        });

        captureException(error, {
          channel: "error",
          area: "auth",
          operation: "verify-email-token",
        });
        setErrorMessage(getErrorMessage(error));
        setVerifyState("error");
      }
    },
    [
      completeOnServer,
      continueToOnboarding,
      firebaseUser,
      logOutToLogin,
      status,
    ],
  );

  useEffect(() => {
    trackEmailVerification("view", { hasToken: !!token, mode });
  }, [mode, token]);

  useEffect(() => {
    if (!isEmailVerificationTokenRoute || status !== "no-user") {
      return;
    }

    trackEmailVerification("auth-restore-grace-started", {
      authStatus: status,
      tokenLength: token.length,
      graceMs: AUTH_RESTORE_GRACE_MS,
    });

    const timeout = setTimeout(() => {
      setAuthRestoreGraceElapsedToken(token);
      trackEmailVerification("auth-restore-grace-elapsed", {
        authStatus: status,
        tokenLength: token.length,
        graceMs: AUTH_RESTORE_GRACE_MS,
      });
    }, AUTH_RESTORE_GRACE_MS);

    return () => clearTimeout(timeout);
  }, [isEmailVerificationTokenRoute, status, token]);

  useEffect(() => {
    const routeStartedAt = now();
    if (isInvalidMode) {
      logTiming("invalid-mode", routeStartedAt, undefined, { mode });
      trackEmailVerification("invalid-mode", { mode });
      return;
    }
    if (!token || processedTokenRef.current === token) {
      logTiming("skip-processing", routeStartedAt, undefined, {
        hasToken: !!token,
        alreadyProcessed: !!token && processedTokenRef.current === token,
      });
      if (!token) {
        trackEmailVerification("idle-no-token");
      }
      return;
    }
    if (isWaitingForAuth) {
      logTiming("wait-for-auth", routeStartedAt, undefined, {
        authStatus: status,
        tokenLength: token.length,
        authRestoreGraceElapsed,
      });
      trackEmailVerification("wait-for-auth", {
        authStatus: status,
        tokenLength: token.length,
        authRestoreGraceElapsed,
      });
      return;
    }
    processedTokenRef.current = token;
    logTiming("received-token", routeStartedAt, undefined, {
      mode,
      authStatus: status,
      tokenLength: token.length,
    });
    trackEmailVerification("token-received", {
      mode,
      authStatus: status,
      tokenLength: token.length,
    });
    void Promise.resolve()
      .then(() => processToken(token))
      .catch(console.error);
  }, [
    mode,
    token,
    processToken,
    isInvalidMode,
    status,
    isWaitingForAuth,
    authRestoreGraceElapsed,
  ]);

  const openSelectedEmailApp = useCallback(async (app: EmailApp) => {
    const url = getEmailAppUrl(app);
    const label = app === "gmail" ? "Gmail" : "Mail";

    if (Platform.OS === "android" && app === "gmail") {
      try {
        IntentLauncher.openApplication(GMAIL_ANDROID_PACKAGE);
        trackEmailVerification("open-email-succeeded", {
          app,
          packageName: GMAIL_ANDROID_PACKAGE,
        });
        return;
      } catch (error) {
        trackEmailVerification("open-email-failed", {
          app,
          packageName: GMAIL_ANDROID_PACKAGE,
          error: error instanceof Error ? error.message : String(error),
        });
        toast.error("Could not open Gmail.");
        throw error;
      }
    }

    if (app !== "gmail") {
      const canOpen = await Linking.canOpenURL(url).catch(() => false);

      if (!canOpen) {
        trackEmailVerification("open-email-failed", { app, scheme: url });
        toast.error(`${label} is not installed.`);
        return;
      }
    }

    try {
      await Linking.openURL(url);
      trackEmailVerification("open-email-succeeded", { app, scheme: url });
    } catch (error) {
      trackEmailVerification("open-email-failed", { app, scheme: url });
      toast.error(`${label} is not installed.`);
      throw error;
    }
  }, []);

  const openEmailClient = useCallback(() => {
    trackEmailVerification("open-email-pressed");
    const handleOpenEmailAppError = (error: unknown) => {
      captureException(error, {
        channel: "error",
        area: "auth",
        operation: "open-email-app",
      });
    };

    if (Platform.OS === "android") {
      openSelectedEmailApp("gmail").catch(handleOpenEmailAppError);
      return;
    }

    Alert.alert(
      "Open email app",
      undefined,
      [
        {
          text: "Mail",
          onPress: () => {
            openSelectedEmailApp("mail").catch(handleOpenEmailAppError);
          },
        },
        {
          text: "Gmail",
          onPress: () => {
            openSelectedEmailApp("gmail").catch(handleOpenEmailAppError);
          },
        },
      ],
      { cancelable: true },
    );
  }, [openSelectedEmailApp]);

  const effectiveVerifyState: VerifyState = isInvalidMode
    ? "error"
    : verifyState;
  const effectiveErrorMessage = isInvalidMode
    ? "This link is not an email verification link."
    : errorMessage;
  const title =
    effectiveVerifyState === "error" ? "Check your link" : "Check your email";
  const body =
    effectiveVerifyState === "error"
      ? effectiveErrorMessage
      : "We sent an email with a link to confirm your email address";

  const showOpenEmailAction = effectiveVerifyState === "idle" || isProcessing;

  return (
    <LScreen safeArea safeAreaEdges={["top", "bottom"]}>
      <LBox flex={1} testID="email-verification-screen">
        <LBox
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          pt="s2"
        >
          <LIcon
            sentryEventName="email_verification_back_to_login"
            name="chevron"
            rotate={90}
            size={32}
            stroke="white"
            fadeEnabled
            disableLoading
            onPress={confirmBackToLogin}
          />
          <LIcon
            colors={{ color: "#F6B452" }}
            sentryEventName="email_verification_show_intercom"
            name="help"
            size={34}
            onPress={async () => {
              trackEmailVerification("help-pressed");
              await Intercom.present();
            }}
          />
        </LBox>

        <LBox flex={1} pt="l2">
          <LBox gap="s3" entering={FadeInDown.duration(400).delay(100)}>
            <LText
              fontSize={32}
              fontFamily={fonts.semiBold}
              color="white"
              lineHeight={35}
              textAlign="center"
            >
              {title}
            </LText>
            <LText
              fontSize={14}
              fontFamily={fonts.medium}
              color="whiteTranslucent60"
              lineHeight={20}
              textAlign="center"
            >
              {body}
            </LText>
            {effectiveVerifyState !== "error" ? (
              <LText
                fontSize={14}
                fontFamily={fonts.medium}
                color="whiteTranslucent60"
                lineHeight={20}
                textAlign="center"
              >
                {emailLabel}
              </LText>
            ) : null}
          </LBox>
        </LBox>

        <LBox gap="s3" pb="l3" entering={FadeInDown.duration(400).delay(200)}>
          {showOpenEmailAction ? (
            <ShellButton
              sentryEventName="email_verification_open_email"
              label="Open email app"
              onPress={openEmailClient}
            />
          ) : null}
        </LBox>

        {isProcessing ? (
          <LBox
            style={[
              StyleSheet.absoluteFill,
              {
                backgroundColor: "rgba(0,0,0,0.78)",
                zIndex: 100,
              },
            ]}
            justifyContent="center"
            alignItems="center"
          >
            <LoadingIndicator />
          </LBox>
        ) : null}
      </LBox>
    </LScreen>
  );
};
